import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, TrendingUp, Building2, Handshake, Calendar } from 'lucide-react';

const PHOTO_URL = 'https://customer-assets.emergentagent.com/job_e6de115d-f3a0-4429-a170-276c80a9816d/artifacts/3pc98tro_Gemini_Generated_Image_mgmfq3mgmfq3mgmf.png';

const HeroSection = () => {
  const [roleIndex, setRoleIndex] = useState(0);
  const roles = [
    'Full-Cycle Enterprise Seller',
    'Pipeline Builder',
    'Net-New Logo Hunter',
    'Complex Deal Navigator'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setRoleIndex((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const metrics = [
    { icon: Target, value: '$6M+', label: 'Cumulative ARR', color: '#8B5CF6' },
    { icon: TrendingUp, value: '110%+', label: 'Avg Quota Attainment', color: '#06B6D4' },
    { icon: Building2, value: '140+', label: 'Net-New Logos', color: '#A78BFA' },
    { icon: Handshake, value: '10+', label: 'ISV & Channel Partners', color: '#8B5CF6' },
    { icon: Calendar, value: '9+', label: 'Years Full-Cycle SaaS', color: '#06B6D4' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-20 pb-20">
      <style>{`
        @keyframes heroPhotoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes heroRingSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes heroGlowPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes heroOrbitCW {
          0% { transform: rotate(0deg) translateX(165px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(165px) rotate(-360deg); }
        }
        @keyframes heroOrbitCCW {
          0% { transform: rotate(0deg) translateX(165px) rotate(0deg); }
          100% { transform: rotate(-360deg) translateX(165px) rotate(360deg); }
        }
      `}</style>

      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left side - Text content */}
          <div className="flex-1">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="text-center lg:text-left mb-12"
            >
              <motion.div 
                variants={itemVariants}
                className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-[#06B6D4]/40 px-6 py-2.5 rounded-full mb-8"
                data-testid="status-badge"
              >
                <div className="w-2 h-2 bg-[#06B6D4] rounded-full animate-pulse" />
                <span className="text-white text-xs sm:text-sm font-medium tracking-wide">OPEN TO OPPORTUNITIES</span>
              </motion.div>

              <motion.h1 
                variants={itemVariants}
                className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-8 text-white"
                data-testid="hero-name"
                style={{ textShadow: '0 0 40px rgba(139, 92, 246, 0.5)' }}
              >
                Vaibhav Vishnu
              </motion.h1>

              <motion.div 
                variants={itemVariants}
                className="h-20 mb-6 flex items-center justify-center lg:justify-start"
              >
                <AnimatePresence mode="wait">
                  <motion.h2
                    key={roleIndex}
                    initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -20, filter: 'blur(8px)' }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white"
                    data-testid="hero-role"
                    style={{ 
                      textShadow: '0 0 30px rgba(167, 139, 250, 0.6), 0 0 60px rgba(139, 92, 246, 0.3)',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {roles[roleIndex]}
                  </motion.h2>
                </AnimatePresence>
              </motion.div>

              <motion.p 
                variants={itemVariants}
                className="text-lg sm:text-xl text-white/90 max-w-3xl mb-12 leading-relaxed"
                data-testid="hero-tagline"
              >
                I don't just sell software, I map organizations, multi-thread stakeholders, and close deals that stick!
              </motion.p>
            </motion.div>

            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-12"
            >
              {metrics.map((metric, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="bg-[#2A1F5E]/40 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center transition-all group relative overflow-hidden"
                  data-testid={`metric-card-${index}`}
                >
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ 
                      background: `radial-gradient(circle at center, ${metric.color}20 0%, transparent 70%)` 
                    }}
                  />
                  
                  <div className="relative z-10">
                    <metric.icon 
                      className="mx-auto mb-4" 
                      size={32} 
                      style={{ color: metric.color, filter: 'drop-shadow(0 0 8px ' + metric.color + '80)' }} 
                    />
                    <div 
                      className="text-3xl sm:text-4xl font-bold font-mono mb-2"
                      style={{ 
                        color: metric.color,
                        textShadow: `0 0 20px ${metric.color}60`
                      }}
                    >
                      {metric.value}
                    </div>
                    <div className="text-xs sm:text-sm text-white/70 leading-tight">{metric.label}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div 
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4"
            >
              <motion.a
                href="#career"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-white text-[#30229E] rounded-lg font-semibold transition-all shadow-lg shadow-white/20 hover:shadow-white/40"
                data-testid="view-performance-btn"
              >
                View My Performance
              </motion.a>
              <motion.a
                href="#contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold transition-all hover:bg-white/10"
                data-testid="contact-btn"
              >
                Let's Talk
              </motion.a>
            </motion.div>
          </div>

          {/* Right side - Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
            className="flex-shrink-0 hidden lg:flex items-center justify-center"
            data-testid="hero-photo-container"
          >
            <div
              className="relative"
              style={{ 
                width: '320px', 
                height: '400px',
                animation: 'heroPhotoFloat 4s ease-in-out infinite'
              }}
            >
              {/* Pulsing glow */}
              <div
                className="absolute rounded-[24px]"
                style={{
                  inset: '-20px',
                  background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.25) 0%, rgba(93, 63, 211, 0.15) 40%, transparent 70%)',
                  animation: 'heroGlowPulse 3s ease-in-out infinite',
                  zIndex: 0
                }}
              />

              {/* Spinning gradient ring - subtle */}
              <div
                className="absolute rounded-[24px]"
                style={{
                  inset: '-2px',
                  background: 'conic-gradient(from 0deg, rgba(139,92,246,0.3), rgba(6,182,212,0.2), rgba(167,139,250,0.3), rgba(34,211,238,0.2), rgba(139,92,246,0.3))',
                  animation: 'heroRingSpin 4s linear infinite',
                  zIndex: 1
                }}
              />

              {/* Inner background to create ring effect */}
              <div
                className="absolute rounded-[22px]"
                style={{
                  inset: '0px',
                  background: 'linear-gradient(135deg, #30229E 0%, #5D3FD3 50%, #CE40CB 100%)',
                  zIndex: 2
                }}
              />

              {/* Photo with theme-blending overlay */}
              <div
                className="absolute overflow-hidden rounded-[22px]"
                style={{
                  inset: '2px',
                  zIndex: 3
                }}
              >
                <img
                  src={PHOTO_URL}
                  alt="Vaibhav Vishnu"
                  className="w-full h-full object-cover object-top"
                  style={{ filter: 'saturate(0.9) brightness(0.95)' }}
                  data-testid="hero-photo"
                />
                {/* Subtle purple tint overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(48,34,158,0.2) 0%, rgba(93,63,211,0.15) 50%, rgba(206,64,203,0.1) 100%)'
                  }}
                />
                {/* Soft edge fade only */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'radial-gradient(ellipse at center, transparent 55%, rgba(48,34,158,0.45) 100%)'
                  }}
                />
              </div>

              {/* Orbiting dots */}
              <div
                className="absolute"
                style={{
                  top: '50%',
                  left: '50%',
                  width: '6px',
                  height: '6px',
                  marginTop: '-3px',
                  marginLeft: '-3px',
                  zIndex: 4,
                  animation: 'heroOrbitCW 8s linear infinite'
                }}
              >
                <div
                  className="rounded-full"
                  style={{ width: '6px', height: '6px', background: 'rgba(139,92,246,0.7)', boxShadow: '0 0 6px rgba(139,92,246,0.5)' }}
                />
              </div>
              <div
                className="absolute"
                style={{
                  top: '50%',
                  left: '50%',
                  width: '6px',
                  height: '6px',
                  marginTop: '-3px',
                  marginLeft: '-3px',
                  zIndex: 4,
                  animation: 'heroOrbitCCW 8s linear infinite'
                }}
              >
                <div
                  className="rounded-full"
                  style={{ width: '6px', height: '6px', background: 'rgba(6,182,212,0.7)', boxShadow: '0 0 6px rgba(6,182,212,0.5)' }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
